// The quiet scroll region's overflow cue (#11426 board-toolbar walkthrough,
// CD scrollbar rule): token surfaces suppress scrollbar paint entirely
// (`fp-scroll-quiet`), and where content genuinely continues past the fold
// the region earns a conditional bottom fade instead — shown only while
// unscrolled overflow exists, ending on the region's own ground, adding
// zero scroll height. The recompute recipe is the board lane's: scroll
// (rAF-debounced), a resize of the region or of any child, and every render,
// so it never goes stale after content changes height without resizing the
// container.

import React, { useCallback, useEffect, useRef, useState } from "react";

export function useScrollFade(): {
  attach: (el: HTMLDivElement | null) => void;
  hasMoreBelow: boolean;
} {
  const elRef = useRef<HTMLDivElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const recompute = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    // 4px of slack absorbs subpixel layout so the fade doesn't flicker at
    // the boundary; a strip that small isn't hidden content worth cueing.
    setHasMoreBelow(el.scrollHeight - el.clientHeight - el.scrollTop > 4);
  }, []);
  const attach = useCallback(
    (el: HTMLDivElement | null) => {
      cleanupRef.current?.();
      elRef.current = el;
      if (!el) return;
      let raf = 0;
      const schedule = () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(recompute);
      };
      el.addEventListener("scroll", schedule);
      // The region and each of its children: content that grows inside a
      // child rendered by another component (the sidebar's history, landing
      // after its request) resizes neither the region nor this component.
      // Children are added and dropped as they come and go, so a removed
      // card is not held until the region unmounts.
      const observer = new ResizeObserver(schedule);
      observer.observe(el);
      for (const child of Array.from(el.children)) observer.observe(child);
      const children = new MutationObserver((records) => {
        for (const record of records) {
          record.addedNodes.forEach((node) => {
            if (node instanceof Element) observer.observe(node);
          });
          record.removedNodes.forEach((node) => {
            if (node instanceof Element) observer.unobserve(node);
          });
        }
        schedule();
      });
      children.observe(el, { childList: true });
      recompute();
      cleanupRef.current = () => {
        el.removeEventListener("scroll", schedule);
        observer.disconnect();
        children.disconnect();
        cancelAnimationFrame(raf);
        cleanupRef.current = null;
      };
    },
    [recompute]
  );
  useEffect(() => () => cleanupRef.current?.(), []);
  // Every render recomputes: cheap, covers content-height changes, and
  // cannot loop — the fade adds zero scroll height, so recompute never
  // changes what it measures.
  useEffect(recompute);
  return { attach, hasMoreBelow };
}

// The fade itself: sticky to the region's bottom edge, cancelling its own
// height so it adds no scroll height, painted down to the region's ground.
// A region with bottom padding passes it as `inset`: sticky stops at the
// padding edge, and the strip below it would show content cut off unfaded.
// A short region passes a shorter `height` so the fade does not cover most
// of what is visible.
export const ScrollFade: React.FC<{
  ground: string;
  inset?: number;
  height?: number;
}> = ({ ground, inset = 0, height = 32 }) => (
  <div
    data-scroll-fade=""
    aria-hidden
    style={{
      position: "sticky",
      bottom: -inset,
      height: `${height}px`,
      marginTop: `-${height}px`,
      background: `linear-gradient(to bottom, transparent, ${ground})`,
      pointerEvents: "none",
    }}
  />
);
